import { useMemo } from 'react';
import type { Entry, Visit } from '@/types/api';
import { isPrimary } from '@/utils/dental';
import { deriveChart } from '@/utils/toothState';
import { isToday } from '../components';

/**
 * Today's open visit, else today's latest one (already confirmed): it stays
 * the one shown until the next entry opens a new visit.
 */
function currentVisitOf(visits: readonly Visit[] | undefined): Visit | null {
  const today = (visits ?? [])
    .filter((v) => isToday(v.startedAt))
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  return today.find((v) => v.status === 'OPEN') ?? today[0] ?? null;
}

/** Everything the chart page derives from the entry list and the visits. */
export function useChartView(entries: Entry[], visits: readonly Visit[] | undefined) {
  return useMemo(() => {
    const visit = currentVisitOf(visits);
    const known = new Set((visits ?? []).map((v) => v.id));
    // Unsent entries, and those of a visit the page has not loaded yet (just
    // opened by the backend), belong to now.
    const isCurrent = (e: Entry) =>
      !!e.local ||
      (!!e.visitId && !known.has(e.visitId) && isToday(e.createdAt)) ||
      (visit ? e.visitId === visit.id : isToday(e.createdAt));
    const byOldest = [...entries].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const current = byOldest.filter(isCurrent);
    return {
      chart: deriveChart(entries, isCurrent),
      visit,
      /** Entries of the visit shown now, oldest first (rejected ones included). */
      current,
      hasHistory: entries.some((e) => !isCurrent(e)),
      onlyPrimary: entries.length > 0 && entries.every((e) => isPrimary(e.tooth)),
    };
  }, [entries, visits]);
}
